// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SignaturePadComponent } from './SignaturePad';
import { PhotoCaptureComponent } from './PhotoCapture';
import { PDFViewer } from './PDFViewer';
import { SignatureService } from '@/services/signatureService';
import { EmailService } from '@/services/emailService';
import { SignatureFormData, SignatureDocument } from '@/types/signature';
import { validateEmail, sanitizeInput, generateHashFromBytes } from '@/utils/signatureUtils';
import { PDFGenerator } from '@/utils/pdfGenerator';
import { FileText, User, Mail, AlertTriangle, CheckCircle, Loader2, Eye, Download } from 'lucide-react';

export const SignatureFormSimple: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [documento, setDocumento] = useState<SignatureDocument | null>(null);
  const [assinatura, setAssinatura] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'success' | 'error'>('form');

  const [formData, setFormData] = useState<SignatureFormData>({
    nome_assinante: '',
    email_assinante: '',
    aceitei_documento: false,
    aceitei_auxilio: false,
    capturar_foto: false,
    assinatura_base64: undefined,
    foto_base64: undefined
  });

  useEffect(() => {
    if (token) {
      loadDocument();
    }
  }, [token]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const doc = await SignatureService.getDocumentByToken(token!);
      
      if (!doc) {
        setError('Documento não encontrado ou link inválido.');
        setStep('error');
        return;
      }

      if (doc.status === 'assinado') {
        setError('Este documento já foi assinado.');
        setStep('error');
        return;
      }

      if (doc.status === 'expirado') {
        setError('Este documento expirou e não pode mais ser assinado.');
        setStep('error');
        return;
      }

      setDocumento(doc);
    } catch (err) {
      console.error('Erro ao carregar documento:', err);
      setError('Erro ao carregar documento. Tente novamente.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SignatureFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.nome_assinante.trim()) {
      setError('Por favor, preencha seu nome completo.');
      return false;
    }

    if (!validateEmail(formData.email_assinante)) {
      setError('Por favor, insira um email válido.');
      return false;
    }

    if (!formData.aceitei_documento) {
      setError('Você deve aceitar que leu e entendeu o documento.');
      return false;
    }

    if (!formData.assinatura_base64) {
      setError('Por favor, assine o documento.');
      return false;
    }

    // Só exigir foto se foi marcado para capturar
    if (formData.capturar_foto && !formData.foto_base64) {
      setError('Por favor, capture sua foto.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Criar assinatura
      // Preparar dados da assinatura (sem foto se não foi capturada)
      const signatureData = {
        ...formData,
        foto_base64: formData.capturar_foto ? formData.foto_base64 : undefined
      };

      // Criar assinatura no banco
      const assinaturaCriada = await SignatureService.createSignature(documento!.id, signatureData);
      setAssinatura(assinaturaCriada);

      // Obter PDF original
      const response = await fetch(documento!.url_pdf_original!);
      if (!response.ok) {
        throw new Error('Erro ao baixar PDF original');
      }
      const originalPDFBytes = await response.arrayBuffer();
      
      // Gerar hash do PDF original
      const hash = generateHashFromBytes(new Uint8Array(originalPDFBytes));
      
      // Gerar PDF assinado (versão simples)
      const signedPDFBytes = await PDFGenerator.generateSignedPDF(
        new Uint8Array(originalPDFBytes),
        {
          nome_assinante: signatureData.nome_assinante,
          email_assinante: signatureData.email_assinante,
          assinatura_base64: signatureData.assinatura_base64!,
          foto_base64: signatureData.foto_base64 || '',
          ip: await SignatureService.getClientIP(),
          user_agent: navigator.userAgent,
          data_assinatura: new Date().toISOString(),
          hash_pdf: hash,
          token_publico: documento!.token_publico
        }
      );

      // Gerar PDF completo com relatório
      const completePDFBytes = await PDFGenerator.generateCompletePDF(
        new Uint8Array(originalPDFBytes),
        {
          nome_assinante: signatureData.nome_assinante,
          email_assinante: signatureData.email_assinante,
          assinatura_base64: signatureData.assinatura_base64!,
          foto_base64: signatureData.foto_base64 || '',
          ip: await SignatureService.getClientIP(),
          user_agent: navigator.userAgent,
          data_assinatura: new Date().toISOString(),
          hash_pdf: hash,
          token_publico: documento!.token_publico
        }
      );

      // Upload do PDF assinado (versão simples)
      const fileName = `signed_${documento!.id}_${Date.now()}.pdf`;
      const uploadResponse = await fetch(`${window.location.origin}/api/documents/upload-signed-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName,
          pdfBytes: Array.from(signedPDFBytes),
          documentId: documento!.id
        })
      });

      if (!uploadResponse.ok) {
        throw new Error('Erro ao fazer upload do PDF assinado');
      }

      const uploadData = await uploadResponse.json();
      const url_pdf_assinado = uploadData.data.url;

      // Upload do PDF completo com relatório
      const completeFileName = `complete_${documento!.id}_${Date.now()}.pdf`;
      const uploadCompleteResponse = await fetch(`${window.location.origin}/api/documents/upload-signed-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: completeFileName,
          pdfBytes: Array.from(completePDFBytes),
          documentId: documento!.id
        })
      });

      if (!uploadCompleteResponse.ok) {
        throw new Error('Erro ao fazer upload do PDF completo');
      }

      const uploadCompleteData = await uploadCompleteResponse.json();
      const url_pdf_completo = uploadCompleteData.data.url;

      // Atualizar assinatura com URLs dos PDFs
      await SignatureService.updateSignedPDF(assinaturaCriada.id, url_pdf_assinado, hash);
      
      // Salvar URL do PDF completo no metadata
      await SignatureService.updateSignatureMetadata(assinaturaCriada.id, {
        url_pdf_completo: url_pdf_completo
      });

      // Marcar documento como assinado
      await SignatureService.markDocumentAsSigned(documento!.id);

      // Enviar email de confirmação se necessário
      if (documento!.requer_confirmacao_email && formData.email_assinante) {
        try {
          await EmailService.sendConfirmationEmail({
            email_assinante: formData.email_assinante,
            nome_assinante: formData.nome_assinante,
            titulo_documento: documento!.titulo,
            token_confirmacao: assinaturaCriada.token_confirmacao,
            link_confirmacao: `${window.location.origin}/confirmar/${assinaturaCriada.token_confirmacao}`
          });
        } catch (emailError) {
          console.error('Erro ao enviar email:', emailError);
        }
      }

      setSuccess('Documento assinado com sucesso!');
      setStep('success');

    } catch (err) {
      console.error('Erro ao processar assinatura:', err);
      setError(err instanceof Error ? err.message : 'Erro ao processar assinatura');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Carregando documento...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Erro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center text-green-600">
              <CheckCircle className="w-5 h-5 mr-2" />
              Documento Assinado com Sucesso!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
            
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Baixar Documentos:</h3>
              
              <div className="grid grid-cols-1 gap-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    // Download do PDF assinado
                    const url = `${window.location.origin}/api/documents/download-signed-pdf?documentId=${documento?.id}&signatureId=${assinatura?.id}`;
                    window.open(url, '_blank');
                  }}
                  disabled={!assinatura}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Assinado (Original + Assinatura)
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    // Download do PDF completo
                    const url = `${window.location.origin}/api/documents/download-complete-pdf?documentId=${documento?.id}&signatureId=${assinatura?.id}`;
                    window.open(url, '_blank');
                  }}
                  disabled={!assinatura}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Completo (Original + Assinatura + Relatório)
                </Button>
              </div>
            </div>
            
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => navigate('/documentos')}
              >
                Voltar aos Documentos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Assinatura Eletrônica
          </h1>
          <p className="text-gray-600">
            Preencha os dados abaixo para assinar o documento
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {documento && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna Esquerda - Informações e Formulário */}
            <div className="lg:col-span-1 space-y-4">
              {/* Informações do Documento - Compacta */}
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Informações do Documento
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <div>
                    <Label className="text-xs font-medium text-gray-600">Título:</Label>
                    <p className="text-sm text-gray-900 font-medium">{documento.titulo}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-600">Data de Criação:</Label>
                    <p className="text-sm text-gray-600">
                      {new Date(documento.data_criacao).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Formulário de Assinatura */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Dados do Assinante
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div>
                    <Label htmlFor="nome" className="text-sm">Nome Completo *</Label>
                    <Input
                      id="nome"
                      type="text"
                      value={formData.nome_assinante}
                      onChange={(e) => handleInputChange('nome_assinante', e.target.value)}
                      placeholder="Digite seu nome completo"
                      className="text-sm"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email_assinante}
                      onChange={(e) => handleInputChange('email_assinante', e.target.value)}
                      placeholder="Digite seu email"
                      className="text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="aceitei_documento"
                        checked={formData.aceitei_documento}
                        onCheckedChange={(checked) => 
                          handleInputChange('aceitei_documento', checked)
                        }
                      />
                      <Label htmlFor="aceitei_documento" className="text-sm">
                        Li e entendi o conteúdo do documento
                      </Label>
                    </div>

                                         <div className="flex items-center space-x-2">
                       <Checkbox
                         id="aceitei_auxilio"
                         checked={formData.aceitei_auxilio}
                         onCheckedChange={(checked) => 
                           handleInputChange('aceitei_auxilio', checked)
                         }
                       />
                       <Label htmlFor="aceitei_auxilio" className="text-sm">
                         Aceito receber auxílio tecnológico se necessário
                       </Label>
                     </div>

                     <div className="flex items-center space-x-2">
                       <Checkbox
                         id="capturar_foto"
                         checked={formData.capturar_foto}
                         onCheckedChange={(checked) => 
                           handleInputChange('capturar_foto', checked)
                         }
                       />
                       <Label htmlFor="capturar_foto" className="text-sm">
                         Quero capturar minha foto para confirmação de identidade
                       </Label>
                     </div>
                   </div>
                 </CardContent>
               </Card>

              {/* Assinatura */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Assinatura</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <SignaturePadComponent
                    onSignatureChange={(signature) => 
                      handleInputChange('assinatura_base64', signature)
                    }
                  />
                </CardContent>
              </Card>

              {/* Foto - Condicional */}
              {formData.capturar_foto && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Foto para Confirmação</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <PhotoCaptureComponent
                      onPhotoChange={(photo) => 
                        handleInputChange('foto_base64', photo)
                      }
                    />
                  </CardContent>
                </Card>
              )}

              {/* Botão de Envio */}
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full"
                size="lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  'Assinar Documento'
                )}
              </Button>
            </div>

            {/* Coluna Direita - Visualização do PDF */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center">
                    <Eye className="w-4 h-4 mr-2" />
                    Visualizar Documento
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {documento.url_pdf_original ? (
                    <PDFViewer pdfUrl={documento.url_pdf_original} />
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Documento não disponível para visualização</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
